/**
 * DLA-20 defaults, scoring, and the hard rules checked before signing
 * (mirrors dla20.go on the server).
 */
import { DLA20_DOMAINS, DLA20_FACTS, type PrpDla20Content } from "@/api/prp.api";
import { parseOnto } from "./use-draft";

export function emptyDla20(previousId = ""): PrpDla20Content {
  return {
    version: "adult",
    assessment_type: previousId ? "reassessment" : "intake",
    previous_assessment_id: previousId,
    items: {},
    facts: {},
    substance_history: "",
    summary: "",
  };
}

export function parseDla20(raw?: string): PrpDla20Content {
  const c = parseOnto(raw, emptyDla20());
  return { ...c, items: c.items ?? {}, facts: c.facts ?? {} };
}

export const scoreOf = (c: PrpDla20Content, key: string) => c.items[key]?.score ?? 0;

/** Average of scored domains and estimated mGAF (average × 10). */
export function dla20Result(c: PrpDla20Content): { average: number; mgaf: number; scored: number } {
  const scores = DLA20_DOMAINS.map((d) => scoreOf(c, d.key)).filter((s) => s >= 1 && s <= 7);
  if (!scores.length) return { average: 0, mgaf: 0, scored: 0 };
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  return { average: Math.round(avg * 100) / 100, mgaf: Math.round(avg * 10), scored: scores.length };
}

const nameOf = (key: string) => DLA20_DOMAINS.find((d) => d.key === key)?.name ?? key;

/** Hard-rule violations for one domain (for inline display). */
export function ruleProblemsFor(c: PrpDla20Content, domain: string): string[] {
  const s = scoreOf(c, domain);
  if (!s) return [];
  const out: string[] = [];
  for (const f of DLA20_FACTS) {
    if (f.domain !== domain) continue;
    if (f.floor) {
      if (!c.facts[f.key] && s < f.value) out.push(`Check "${f.question}" to score below ${f.value}`);
      continue;
    }
    if (!c.facts[f.key]) continue;
    if ((f.exact && s !== f.value) || (!f.exact && s > f.value)) out.push(`${f.question}: ${f.rule}`);
  }
  if (domain === "alcohol_drug") {
    if (c.substance_history === "never" && s !== 7) out.push("No use and no history: score must be 7");
    if (c.substance_history === "current" && s > 4) out.push("Current use: score at most 4");
    if (c.substance_history === "past" && (s < 5 || s > 6)) out.push("No current use, with a history: score 5 or 6");
  }
  return out;
}

export function dla20Problems(c: PrpDla20Content): string[] {
  const p: string[] = [];
  if (!["intake", "reassessment", "discharge"].includes(c.assessment_type)) p.push("Assessment type");
  if (!["current", "past", "never"].includes(c.substance_history)) p.push("Substance use history");
  for (const d of DLA20_DOMAINS) {
    const item = c.items[d.key];
    const s = item?.score ?? 0;
    if (s < 1 || s > 7) p.push(`${d.name}: score 1–7`);
    else if (s <= 4 && !item?.narrative?.trim()) p.push(`${d.name}: explain the impairment score`);
    for (const r of ruleProblemsFor(c, d.key)) p.push(`${nameOf(d.key)} - ${r}`);
  }
  if (!c.summary.trim()) p.push("Summary");
  return p;
}
